import { useNavigate } from "react-router-dom"
import LokaMark from "@/components/LokaMark/LokaMark"
import RoundRailIcon from "@/components/RoundRailIcon/RoundRailIcon"
import type { RailRound } from "@/components/RoundRailIcon/RoundRailIcon"
import SegmentedControl from "@/components/SegmentedControl/SegmentedControl"
import Tooltip from "@/components/Tooltip/Tooltip"
import { NAV_COPY, PHASE_COPY, ROUND_COPY, displayPhase, switchablePhases } from "@/content/rounds"
import type { SessionView } from "@/hooks/useSessionView"
import { ROUTES } from "@/routes/routes"
import { useSessionStore } from "@/store/sessionStore"
import { activeRounds, roundStanding, selectSession } from "@/store/selectors"
import type { FlowStanding } from "@/store/selectors"
import type { Phase } from "@/types"
import styles from "@/styles/components/SessionHeader.module.css"

/*
 * The mark, the round rail, and the phases of the round on screen.
 *
 * Two floating pills rather than one bar. The phases are lifted out of the rail
 * into their own `SegmentedControl` — they are a different question (where in
 * this round) from the rail's (which round), and putting them inside the round's
 * own pill made a control that grew and shrank as the room moved.
 *
 * NOTHING HERE WRITES. The rail used to move the whole room, which meant it was
 * facilitator-only and that a guest had no way at all to check what they had
 * voted for ten minutes ago. It now moves this screen and no other: back through
 * finished rounds, read-only, identically for both surfaces — so it takes no
 * `canDrive`, needs no spinner and can never leave the room somewhere nobody
 * asked to be.
 *
 * Forward is not navigation. A round ahead of the room is refused rather than
 * greyed out quietly, because arriving early is not a view of an empty screen —
 * `RoundControls` prepares the documents a round needs on the way in, and the
 * only way to reach one is to let it.
 */

interface SessionHeaderProps {
  /** Where this browser is looking. Owned by `Session` — see `useSessionView`. */
  view: SessionView
}

export default function SessionHeader({ view }: SessionHeaderProps) {
  const session = useSessionStore(selectSession)
  const navigate = useNavigate()

  if (!session) return null

  const onLobby = session.round === "lobby"
  const rail = activeRounds(session.config).filter((round): round is RailRound => round !== "lobby")

  /*
   * The chips belong to the round ON SCREEN, not to the room's. While a finished
   * round is being read back they are inert: it is shown in the state it was
   * left in, and stepping around inside something that is already over is a
   * second axis of navigation that answers nothing.
   */
  const inLiveRound = view.round === session.round
  const phases = switchablePhases(view.round)
  const livePhase = displayPhase(session.round, session.phase)
  const shownPhase = displayPhase(view.round, view.phase)

  /*
   * Which chip counts as the room's, by the LIT one rather than by
   * `session.phase`. The two differ on the phases that are states rather than
   * steps — the Vote chip stays lit through the tally — and a chip that is lit
   * and also offers to take you backwards is a contradiction the room does not
   * need to work out.
   */
  const liveIndex = livePhase === null ? -1 : phases.indexOf(livePhase)

  const phaseStanding = (index: number): FlowStanding => {
    if (!inLiveRound) return "done"
    if (index === liveIndex) return "live"
    return index < liveIndex ? "done" : "ahead"
  }

  const goToRound = (round: RailRound, standing: FlowStanding) => {
    if (standing === "live") view.live()
    if (standing === "done") view.review(round)
  }

  return (
    <header className={styles.header}>
      <button
        type="button"
        className={styles.home}
        onClick={() => navigate(ROUTES.ROOT)}
        aria-label="Loka AI Cards — back to customers"
      >
        <LokaMark className={styles.mark} />
      </button>

      {onLobby ? null : (
        <>
          <ol className={styles.rail}>
            {rail.map((round) => {
              const label = ROUND_COPY[round].label
              const standing = roundStanding(session, round)
              const isShown = round === view.round
              const tip = NAV_COPY[standing](label)

              return (
                <li key={round} className={styles.step}>
                  {/* One string, two jobs: the bubble a sighted person reads and
                      the name the button is announced by. `Tooltip` is
                      `aria-hidden` so they cannot be heard twice. */}
                  <Tooltip label={tip}>
                    <button
                      type="button"
                      className={[
                        styles.stepButton,
                        isShown ? styles.stepShown : "",
                        standing === "ahead" ? styles.stepAhead : "",
                        standing === "done" && !isShown ? styles.stepDone : "",
                        /* The room's own round, when it is not the one on
                         * screen. Without it a review hides the one fact the
                         * rail exists to show — where everybody else is. */
                        standing === "live" && !isShown ? styles.stepLive : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      /*
                       * `aria-disabled`, never `disabled`. A locked pill has to
                       * stay hoverable or the tooltip explaining WHY it is
                       * locked never appears — a disabled button dispatches no
                       * pointer events at all, so the one control that most
                       * needs to explain itself would be the one that could
                       * not.
                       */
                      aria-disabled={standing === "ahead" || undefined}
                      aria-current={round === session.round ? "step" : undefined}
                      aria-label={tip}
                      // A hook that copy changes cannot break, for tests and styling.
                      data-round={round}
                      data-standing={standing}
                      onClick={() => goToRound(round, standing)}
                    >
                      <RoundRailIcon round={round} className={styles.icon} />
                      {/* Only the round on screen is named; the rest read as icons. */}
                      {isShown ? <span className={styles.stepName}>{label}</span> : null}
                    </button>
                  </Tooltip>
                </li>
              )
            })}
          </ol>

          {phases.length > 1 ? (
            <SegmentedControl
              label={`Phase of ${ROUND_COPY[view.round].label}`}
              value={shownPhase}
              size="sm"
              onChange={(phase) => {
                const index = phases.indexOf(phase as Phase)
                const standing = phaseStanding(index)
                if (standing === "live") view.live()
                if (standing === "done") view.review(view.round, phase as Phase)
              }}
              options={phases.map((phase, index) => {
                const labels: Record<string, string> = PHASE_COPY[view.round]
                const label = labels[phase] ?? phase
                const standing = phaseStanding(index)
                const tip = !inLiveRound
                  ? NAV_COPY.frozenPhase(label)
                  : standing === "live"
                    ? NAV_COPY.livePhase(label)
                    : standing === "done"
                      ? NAV_COPY.donePhase(label)
                      : NAV_COPY.aheadPhase(label)

                return {
                  value: phase,
                  label,
                  title: tip,
                  // Same reason as the rail: locked has to be able to say why.
                  locked: !inLiveRound || standing === "ahead",
                }
              })}
            />
          ) : null}

          {/*
           * The way back, and the only thing on this header that appears and
           * disappears. It is not in the rail: the rail says where you are, and
           * this says that where you are is not where everyone else is.
           */}
          {view.reviewing ? (
            <button type="button" className={styles.live} onClick={view.live}>
              <span className={styles.liveDot} aria-hidden="true" />
              {NAV_COPY.backToLive}
              <span className={styles.liveWhere}>{ROUND_COPY[session.round].label}</span>
            </button>
          ) : null}
        </>
      )}
    </header>
  )
}
